import { FormGroup, Infobox, Label } from '@zoom/components';
import { isSameDay, min } from 'date-fns';
import { AnyObject } from 'final-form';
import { equals } from 'ramda';
import React from 'react';
import { Field, FieldMetaState } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../app/AuthorizationProvider';
import { ComposedInfobox } from '../../../components/ComposedInfobox/ComposedInfobox';
import { DatePickerField } from '../../../components/DatePicker';
import { TimePickerField } from '../../../components/DatePicker/TimePicker';
import { finalFormInputHelper } from '../../../components/Inputs/RenderInput';
import { finalFormNumberInputHelper } from '../../../components/Inputs/RenderNumberInput';
import { createEndOfDateRange, handleEndOfDateRange } from '../../../utils/dateRange';
import { ONE_MINUTE_IN_SECONDS, identifyTimeFormat } from '../../../utils/time';
import { max255chars, required } from '../../../utils/validations';
import { composeValidators } from '../../../utils/validations/composeValidators';
import { inRange } from '../../../utils/validations/inRange';
import { getDateTime } from '../../Scheduling/SchedulingComposer/ScheduleConfigurationSidebar';
import { ADJUSTMENTS_CONSTRAINS } from '../config';

interface AdjustmentFormProps extends React.PropsWithChildren {
  isChatQueueType: boolean;
  maxDate: Date;
  minDate: Date;
  values: AnyObject;
}

export const AdjustmentForm: React.FC<AdjustmentFormProps> = ({
  children,
  isChatQueueType,
  maxDate,
  minDate,
  values,
}) => {
  const { t } = useTranslation();
  const { locale } = useAuth();

  const timeFormat = identifyTimeFormat(locale);
  const maxEndTime = createEndOfDateRange(maxDate);
  const { endDateTime, endTime, startDateTime, startTime } = values;

  const startWithTime = startDateTime && startTime && (getDateTime(startTime, startDateTime.getTime()) as number);
  const endWithTime = endDateTime && endTime && (getDateTime(endTime, endDateTime.getTime()) as number);

  const validateTimeWithMaxRange = (a = 0, b = 0, endRangeMax = 0): FieldMetaState<any> => {
    return a >= b || b > endRangeMax
      ? {
          valid: false,
          modified: true,

          // To satisfy DatePicker conditions
          invalid: true,
          touched: true,
        }
      : {};
  };

  const meta = validateTimeWithMaxRange(startWithTime, endWithTime, maxDate.getTime());

  return (
    <div className="adjustment-form-container" data-test-id="adjustment-form">
      <p data-test-id="adjustments-sidebar:title">{t('forecast.adjustments')}</p>
      <FormGroup>
        <Field
          name="name"
          validate={composeValidators(required, max255chars)}
          render={finalFormInputHelper({
            label: t('forecast.adjustmentsName'),
            dataTestId: 'adjustment-form:name',
            type: 'text',
            disabled: false,
          })}
        />
      </FormGroup>
      <FormGroup>
        <Label label={t('forecast.applyChangesToTheDateRange')} />
        <div className="row">
          <div className="col-6">
            <Field
              component={DatePickerField({
                minDate,
                dataTestId: 'adjustment-form:date-from',
                label: t('date.dateFrom'),
                selectsStart: true,
                startDate: values.startDateTime,
                endDate: values.endDateTime && handleEndOfDateRange(values.endDateTime),
                maxDate: handleEndOfDateRange(min([values.endDateTime, maxDate])),
                noValidation: !meta.invalid,
              })}
              isEqual={equals}
              name="startDateTime"
              validate={required}
            />
          </div>
          <div className="col-6">
            <TimePickerField
              data-test-id="adjustment-form:time-from"
              label={t('date.timeFrom')}
              meta={meta}
              name="startTime"
              timeFormat={timeFormat}
              validate={required}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <Field
              component={DatePickerField({
                maxDate: maxEndTime,
                dataTestId: 'adjustment-form:date-to',
                label: t('date.dateTo'),
                selectsEnd: true,
                startDate: values.startDateTime,
                endDate: values.endDateTime,
                minDate: values.startDateTime ? values.startDateTime : minDate,
                noValidation: !meta.invalid,
              })}
              isEqual={equals}
              name="endDateTime"
              validate={required}
            />
          </div>
          <div className="col-6">
            <TimePickerField
              data-test-id="adjustment-form:time-to"
              label={t('date.timeTo')}
              max={isSameDay(values.endDateTime, maxDate) ? ONE_MINUTE_IN_SECONDS * 15 : undefined}
              meta={meta}
              min={0}
              name="endTime"
              timeFormat={timeFormat}
              validate={required}
            />
          </div>
        </div>
        {meta.invalid && (
          <ComposedInfobox mainMessageType="error">{t('forecast.adjustmentRangeInvalid')}</ComposedInfobox>
        )}
        {children}
      </FormGroup>
      <FormGroup className="two-columns-wrapper">
        <Label input={{ name: 'contactVolume' }} label={`${t('forecast.forecastedContactVolume')}:`} />
        <Field
          name="contactVolume"
          validate={composeValidators(
            required,
            inRange(ADJUSTMENTS_CONSTRAINS.contactVolume.min, ADJUSTMENTS_CONSTRAINS.contactVolume.max)
          )}
          render={finalFormNumberInputHelper({
            dataTestId: 'adjustment-form:contact-volume',
            type: 'number',
            disabled: false,
            isPercentage: true,
            min: ADJUSTMENTS_CONSTRAINS.contactVolume.min,
            max: ADJUSTMENTS_CONSTRAINS.contactVolume.max,
          })}
        />
      </FormGroup>
      <FormGroup className="two-columns-wrapper">
        <Label input={{ name: 'averageHandlingTime' }} label={`${t('forecast.forecastedAHT')}:`} />
        <Field
          name="averageHandlingTime"
          validate={composeValidators(
            required,
            inRange(ADJUSTMENTS_CONSTRAINS.averageHandlingTime.min, ADJUSTMENTS_CONSTRAINS.averageHandlingTime.max)
          )}
          render={finalFormNumberInputHelper({
            dataTestId: 'adjustment-form:average-handling-time',
            type: 'number',
            disabled: false,
            isPercentage: true,
            min: ADJUSTMENTS_CONSTRAINS.averageHandlingTime.min,
            max: ADJUSTMENTS_CONSTRAINS.averageHandlingTime.max,
          })}
        />
      </FormGroup>
      <FormGroup className="two-columns-wrapper">
        <Label input={{ name: 'shrinkagePercentage' }} label={`${t('forecast.shrinkage')}:`} />
        <Field
          name="shrinkagePercentage"
          validate={composeValidators(
            required,
            inRange(ADJUSTMENTS_CONSTRAINS.shrinkage.min, ADJUSTMENTS_CONSTRAINS.shrinkage.max)
          )}
          render={finalFormNumberInputHelper({
            dataTestId: 'adjustment-form:shrinkage',
            type: 'number',
            disabled: false,
            isPercentage: true,
            min: ADJUSTMENTS_CONSTRAINS.shrinkage.min,
            max: ADJUSTMENTS_CONSTRAINS.shrinkage.max,
          })}
        />
      </FormGroup>
      {isChatQueueType && (
        <>
          <FormGroup className="two-columns-wrapper">
            <Label input={{ name: 'concurrentChatsCount' }} label={`${t('forecast.concurrentChats')}:`} />
            <Field
              name="concurrentChatsCount"
              validate={composeValidators(
                required,
                inRange(ADJUSTMENTS_CONSTRAINS.concurrentChats.min, ADJUSTMENTS_CONSTRAINS.concurrentChats.max)
              )}
              render={finalFormNumberInputHelper({
                dataTestId: 'adjustment-form:concurrent-chats',
                type: 'number',
                min: ADJUSTMENTS_CONSTRAINS.concurrentChats.min,
                max: ADJUSTMENTS_CONSTRAINS.concurrentChats.max,
              })}
            />
          </FormGroup>
          <Infobox>{t('forecast.concurrentChatsCountInfo')}</Infobox>
        </>
      )}
    </div>
  );
};
